import { useSearch } from "@tanstack/react-router";
import { Button, Col, Row } from "antd";
import {
    useBatchInsertFormFieldsMutation,
    useBatchUpdateFormFieldsMutation,
    useBatchDeleteFormFieldsMutation,
} from "api/TenantSlug/FormField/hooks.ts";
import { useGetFormStructureQuery } from "api/TenantSlug/FormStructure/hooks.ts";
import { FormFieldSettingsDrawer } from "components/Drawers/FormFieldSettingsDrawer/FormFieldSettingsDrawer.tsx";
import { FormFieldsEditorItem } from "components/Forms/FormFieldsForm/FormFieldsEditorItem.tsx";
import { GeneralFormProvider } from "components/UI/GeneralForm/GeneralFormProvider";
import { useGeneralForm } from "components/UI/GeneralForm/hooks/useGeneralForm.ts";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { FC, useEffect, useState } from "react";
import { FieldErrors, useFieldArray } from "react-hook-form";
import { toast } from "react-toastify";
import { FormFieldTypes } from "types/enums";
import { DrawerId, FormId } from "types/models.ts";
import { z } from "zod";

import { formSchema, FormStructureFormData } from "./formSchema.ts";

import FormStructureData = App.Data.FormStructureData;
import StoreFormFieldRequest = App.Http.Requests.FormField.StoreFormFieldRequest;
import UpdateFormFieldRequest = App.Http.Requests.FormField.UpdateFormFieldRequest;

interface FormStructureDetailProps {
    formStructureId: string;
}

interface FormFieldsFormContentProps {
    formStructure: FormStructureData;
    deletedIds: Array<string>;
    setDeletedIds: (ids: Array<string> | ((prevIds: Array<string>) => Array<string>)) => void;
}

interface SearchParams {
    [`fieldSettings-formFieldId`]: string;
    [`fieldSettings-formStructureId`]: string;
    [`fieldSettings-type`]: string;
}

const mapFormFieldToFormState = (field: NonNullable<FormStructureData["formFields"]>[number]) => ({
    ...field,
    id: field.id ?? undefined,
    formFieldSelectItems: field.formFieldSelectItems || [],
    validation_scenarios:
        field.validationScenarios
            ?.map((scenario, index) => ({ id: scenario.id || "", order: index }))
            .filter((s) => s.id) || [],
    modification_scenarios:
        field.modificationScenarios
            ?.map((scenario, index) => ({ id: scenario.id || "", order: index }))
            .filter((s) => s.id) || [],
    autocomplete_scenarios:
        field.autocompleteScenarios
            ?.map((scenario, index) => ({ id: scenario.id || "", order: index }))
            .filter((s) => s.id) || [],
});

export const FormFieldsFormContent: FC<FormFieldsFormContentProps> = ({ formStructure, deletedIds, setDeletedIds }) => {
    const { t } = usePaldockTranslation("forms.form-fields");
    const generalFormUtils = useGeneralForm<FormStructureFormData>();
    const { control, reset } = generalFormUtils;

    // Reset form when formStructure changes (after refetch)
    useEffect(() => {
        if (formStructure) {
            reset({
                formFields: formStructure.formFields?.map(mapFormFieldToFormState),
            });
        }
    }, [formStructure, reset]);

    const { fields, append, remove } = useFieldArray<FormStructureFormData>({
        control,
        name: "formFields",
    });
    const searchParams = useSearch({ strict: false }) as SearchParams;

    const newField = {
        is_required: false,
        default_value: null,
        doc_note: null,
        example_value: null,
        placeholder: null,
        form_structure_id: formStructure.id,
        frontend_format: null,
        max: null,
        min: null,
        not_show_if_system_name_equals: null,
        page: null,
        parent_form_field_id: null,
        show_only_if_system_name_equals: null,
        system_name: "",
        type: FormFieldTypes.Text,
        validation_regex: null,
        validation_sequence_step_id: null,
        data_modification_sequence_step_id: null,
        autocomplete_sequence_step_id: null,
        validation_scenarios: [],
        modification_scenarios: [],
        autocomplete_scenarios: [],
        data_modification_accessor: null,
        autocomplete_accessor: null,
        visible_description: "",
        formFieldSelectItems: [],
    };

    return (
        <>
            <div style={{ display: "flex", flexDirection: "column", rowGap: 16 }}>
                {fields.map(({ id }, index) => {
                    return (
                        <Row key={id} gutter={16} className="py-2">
                            <Col span={24}>
                                <FormFieldsEditorItem
                                    index={index}
                                    onDelete={() => remove(index)}
                                    formStructureId={formStructure?.id || ""}
                                    deletedIds={deletedIds}
                                    setDeletedIds={setDeletedIds}
                                    // onUpdate={(updatedField) => handleFieldUpdate(id, updatedField)}
                                />
                            </Col>
                        </Row>
                    );
                })}
            </div>
            <Row>
                <Col>
                    <Button type="primary" className="mt-5" onClick={() => append(newField)} block>
                        {t("buttons.add_item")}
                    </Button>
                </Col>
            </Row>
            {searchParams[`${DrawerId.FieldSettings}-formFieldId`] && (
                <FormFieldSettingsDrawer
                    formFieldId={searchParams[`${DrawerId.FieldSettings}-formFieldId`]}
                    type={searchParams[`${DrawerId.FieldSettings}-type`] as FormFieldTypes}
                    formStructureId={searchParams[`${DrawerId.FieldSettings}-formStructureId`]}
                />
            )}
        </>
    );
};

export const FormFieldsForm: FC<FormStructureDetailProps> = ({ formStructureId }) => {
    const { t } = usePaldockTranslation("forms.form-fields");
    const [deletedIds, setDeletedIds] = useState<Array<string>>([]);
    const batchInsertFormFieldsMutation = useBatchInsertFormFieldsMutation();
    const batchUpdateFormFieldsMutation = useBatchUpdateFormFieldsMutation();
    const batchDeleteFormFieldsMutation = useBatchDeleteFormFieldsMutation();
    const getFormStructureQuery = useGetFormStructureQuery(formStructureId, {
        filter: {
            include:
                "formFields,formFields.formFieldSelectItems,formFields.validationScenarios,formFields.modificationScenarios,formFields.autocompleteScenarios",
        },
    });
    const formStructure = getFormStructureQuery.data?.formStructure;
    const formId = FormId.FormFieldsForm;

    const handleSubmit = async (values: FormStructureFormData) => {
        if (!values.formFields) return;

        // First, handle deletions
        if (deletedIds.length > 0) {
            try {
                await new Promise<void>((resolve, reject) => {
                    batchDeleteFormFieldsMutation.mutate(
                        { ids: deletedIds },
                        {
                            onSuccess: () => {
                                toast.success(t("messages.delete_success", { count: deletedIds.length }));
                                setDeletedIds([]);
                                resolve();
                            },
                            onError: (error) => {
                                toast.error(t("messages.delete_error"));
                                reject(error);
                            },
                        }
                    );
                });
            } catch (error) {
                return; // Exit if delete fails
            }
        }

        // Existing fields keep their weight; new fields go above the highest one, which avoids
        // the unique (form_structure_id, weight) key (the editor can only append, not reorder)
        const newFields: StoreFormFieldRequest[] = [];
        const existingFields: UpdateFormFieldRequest[] = [];
        const maxWeight = Math.max(-1, ...(formStructure?.formFields ?? []).map((field) => field.weight ?? -1));

        values.formFields.forEach((field) => {
            // Skip deleted fields
            if (field.id && deletedIds.includes(field.id)) {
                return;
            }
            if (!field.id) {
                // New field - add to newFields array
                newFields.push({
                    ...field,
                    weight: maxWeight + 1 + newFields.length,
                    validation_sequence_step_id: field.validation_sequence_step_id ?? null,
                    data_modification_sequence_step_id: field.data_modification_sequence_step_id ?? null,
                    autocomplete_sequence_step_id: field.autocomplete_sequence_step_id ?? null,
                    validation_scenarios: field.validation_scenarios || [],
                    modification_scenarios: field.modification_scenarios || [],
                    autocomplete_scenarios: field.autocomplete_scenarios || [],
                });
            } else {
                // Existing field - add to existingFields array
                const updateField: UpdateFormFieldRequest = {
                    ...field,
                    id: field.id,
                    system_name: field.system_name,
                    is_required: field.is_required,
                    is_hidden: field.is_hidden,
                    visible_description: field.visible_description,
                    doc_note: field.doc_note,
                    type: field.type,
                    validation_scenarios: field.validation_scenarios || [],
                    modification_scenarios: field.modification_scenarios || [],
                    autocomplete_scenarios: field.autocomplete_scenarios || [],
                };

                // Only include non-null values for PATCH request
                if (field.default_value !== null) updateField.default_value = field.default_value;
                if (field.example_value !== null) updateField.example_value = field.example_value;
                if (field.placeholder !== null) updateField.placeholder = field.placeholder;
                if (field.form_structure_id !== null) updateField.form_structure_id = field.form_structure_id;
                if (field.frontend_format !== null) updateField.frontend_format = field.frontend_format;
                if (field.max !== null) updateField.max = field.max;
                if (field.min !== null) updateField.min = field.min;
                if (field.not_show_if_system_name_equals !== null)
                    updateField.not_show_if_system_name_equals = field.not_show_if_system_name_equals;
                if (field.show_only_if_system_name_equals !== null)
                    updateField.show_only_if_system_name_equals = field.show_only_if_system_name_equals;
                if (field.validation_regex !== null) updateField.validation_regex = field.validation_regex;
                // Always include sequence step fields (including null values for clearing)
                updateField.page = field.page ?? null;
                updateField.validation_sequence_step_id = field.validation_sequence_step_id ?? null;
                updateField.data_modification_sequence_step_id = field.data_modification_sequence_step_id ?? null;
                updateField.autocomplete_sequence_step_id = field.autocomplete_sequence_step_id ?? null;
                updateField.validation_scenarios = field.validation_scenarios || [];
                updateField.modification_scenarios = field.modification_scenarios || [];
                updateField.autocomplete_scenarios = field.autocomplete_scenarios || [];
                updateField.data_modification_accessor = field.data_modification_accessor ?? null;
                updateField.autocomplete_accessor = field.autocomplete_accessor ?? null;

                existingFields.push(updateField);
            }
        });

        try {
            // Process new fields with batch insert
            if (newFields.length > 0) {
                await new Promise<void>((resolve, reject) => {
                    batchInsertFormFieldsMutation.mutate(
                        { formFields: newFields },
                        {
                            onSuccess: () => {
                                toast.success(t("messages.create_success", { count: newFields.length }));
                                resolve();
                            },
                            onError: reject,
                        }
                    );
                });
            }

            // Process existing fields with batch update
            if (existingFields.length > 0) {
                await new Promise<void>((resolve, reject) => {
                    batchUpdateFormFieldsMutation.mutate(
                        { formFields: existingFields },
                        {
                            onSuccess: () => {
                                toast.success(t("messages.update_success", { count: existingFields.length }));
                                resolve();
                            },
                            onError: reject,
                        }
                    );
                });
            }

            await getFormStructureQuery.refetch();
        } catch (error) {
            if (error instanceof z.ZodError) {
                error.errors.forEach((err) => toast.error(err.message));
            } else {
                console.error(error);
            }
        }
    };

    // The drawers with the fields are usually closed at submit, so a rejected field is named in a toast
    const handleInvalid = (errors: FieldErrors<FormStructureFormData>, values: FormStructureFormData) => {
        const fieldErrors = Array.isArray(errors.formFields) ? errors.formFields : [];
        fieldErrors.forEach((fieldError, index) => {
            const messages = Object.values(fieldError ?? {}).map((error) => (error as { message?: unknown })?.message);
            const message = messages.find((value): value is string => typeof value === "string");
            if (message) {
                toast.error(`${values.formFields?.[index]?.system_name || `#${index + 1}`}: ${message}`);
            }
        });
    };

    if (!formStructure || !formStructure?.formFields) {
        return null;
    }

    return (
        <GeneralFormProvider<FormStructureFormData>
            id={formId}
            schema={formSchema}
            resetOnSubmit={false}
            defaultValues={{
                formFields: formStructure?.formFields.map(mapFormFieldToFormState),
            }}
            onSubmit={handleSubmit}
            onInvalid={handleInvalid}
        >
            <FormFieldsFormContent
                formStructure={formStructure}
                deletedIds={deletedIds}
                setDeletedIds={setDeletedIds}
            />
        </GeneralFormProvider>
    );
};
